/**
 * Mesin keadaan unggah bukti — MURNI, tanpa fetch dan tanpa DOM.
 *
 * Ini adalah penjaga `TASK-006` di sisi klien. Aturan tunggal yang menentukan seluruh modul:
 *
 * > **`complete` tidak boleh dipanggil selama masih ada berkas yang belum `uploaded`.**
 *
 * Server tetap menegakkannya — finalisasi memanggil `headObject` ke Supabase dan mencocokkan
 * checksum, lalu menolak `TASK-006` bila bukti tidak terverifikasi. Yang dicegah di sini adalah
 * penyelesaian SEBAGIAN yang diam-diam: 2 dari 3 foto berhasil, jaringan putus, dan tugas
 * terlanjur ditandai selesai. Kurirnya pulang, dan bukti yang hilang baru ketahuan berminggu
 * kemudian saat pelanggan menyanggah.
 *
 * Alur per berkas tidak boleh dipendekkan (API-CONTRACT §4):
 *   idle → hashing → requesting-target → uploading → uploaded
 * Server yang menentukan `bucket`, `objectKey`, dan `fileId`; klien tidak pernah menebaknya.
 */

import { formatAction } from './task-actions';

export type UploadPhase =
  'idle' | 'hashing' | 'requesting-target' | 'uploading' | 'uploaded' | 'failed';

export interface UploadSlot {
  /** Identitas lokal, stabil selama sesi — BUKAN `fileId` dari server. */
  readonly localId: string;
  readonly kind: 'photo' | 'signature';
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly mimeType: string;
  readonly phase: UploadPhase;
  /** Diisi server saat `upload-targets` dijawab. `null` sampai saat itu. */
  readonly fileId: string | null;
  /** 0..100, hanya bermakna saat `uploading`. */
  readonly progress: number;
  readonly errorMessage: string | null;
}

export function newSlot(input: {
  localId: string;
  kind: 'photo' | 'signature';
  fileName: string;
  sizeBytes: number;
  mimeType: string;
}): UploadSlot {
  return {
    ...input,
    phase: 'idle',
    fileId: null,
    progress: 0,
    errorMessage: null,
  };
}

/** Transisi yang sah — mencerminkan urutan tiga langkah, bukan menyingkatnya. */
export function advance(slot: UploadSlot, phase: UploadPhase, fileId?: string): UploadSlot {
  return {
    ...slot,
    phase,
    fileId: fileId ?? slot.fileId,
    errorMessage: phase === 'failed' ? slot.errorMessage : null,
    progress: phase === 'uploaded' ? 100 : slot.progress,
  };
}

export function markFailed(slot: UploadSlot, message: string): UploadSlot {
  return { ...slot, phase: 'failed', errorMessage: message };
}

/**
 * Mengulang satu berkas — kembali ke awal, BUKAN melanjutkan dari tengah.
 *
 * `fileId` lama dibuang: target unggah punya masa berlaku, dan memakai ulang target yang sudah
 * kedaluwarsa menghasilkan kegagalan finalisasi yang jauh lebih membingungkan daripada meminta
 * target baru.
 */
export function retrySlot(slot: UploadSlot): UploadSlot {
  return { ...slot, phase: 'idle', fileId: null, progress: 0, errorMessage: null };
}

export function withProgress(slot: UploadSlot, progress: number): UploadSlot {
  return { ...slot, progress: Math.max(0, Math.min(100, progress)) };
}

/** Berkas yang sudah benar-benar terunggah dan punya `fileId` dari server. */
export function isUploaded(slot: UploadSlot): boolean {
  return slot.phase === 'uploaded' && slot.fileId !== null;
}

export function isBusy(slot: UploadSlot): boolean {
  return (
    slot.phase === 'hashing' || slot.phase === 'requesting-target' || slot.phase === 'uploading'
  );
}

export interface ProofRequirements {
  /** Dari `task_type.requires_proof`. Bila `false`, bukti bersifat opsional. */
  readonly requiresProof: boolean;
  /**
   * Apakah tanda tangan penerima MENGUNCI penyelesaian.
   *
   * Dipisahkan dari `requiresProof` supaya keputusannya terlihat dan dapat dibalik di satu
   * tempat, bukan dihapus diam-diam dari daftar penghalang. Server tidak pernah menuntutnya.
   */
  readonly requiresSignature: boolean;
  /**
   * Status tugas saat ini. Opsional supaya pemanggil lama tetap sah — bila tidak diberikan,
   * penghalang "belum tiba" tidak dinilai sama sekali, bukan dianggap terpenuhi.
   */
  readonly statusTugas?: string;
  readonly recipientName: string;
}

export interface CompletionBlocker {
  readonly code:
    | 'upload_in_progress'
    | 'upload_failed'
    | 'signature_missing'
    | 'recipient_missing'
    | 'photo_missing'
    | 'not_arrived';
  readonly message: string;
}

/**
 * Alasan mengapa `complete` BELUM boleh dipanggil. Kosong berarti boleh.
 *
 * Mengembalikan daftar, bukan boolean: kurir yang diberi tahu "belum bisa" tanpa sebab akan
 * menekan tombol berulang kali. Setiap penghalang punya tindakan yang berbeda — menunggu,
 * mengulang unggah, atau mengisi nama.
 */
export function completionBlockers(
  slots: readonly UploadSlot[],
  requirements: ProofRequirements,
): readonly CompletionBlocker[] {
  const blockers: CompletionBlocker[] = [];

  /*
   * TUGAS BELUM DITANDAI TIBA — dulu tombolnya mati untuk keadaan ini TANPA satu pun keterangan.
   * Kurir melihat tombol kelabu dan tidak punya cara mengetahui apa yang kurang; daftar penghalang
   * hanya memuat soal unggahan dan bukti. Tombol mati yang tidak menjelaskan dirinya adalah
   * kebuntuan, bukan pengaman.
   *
   * Nama tombolnya diambil dari `formatAction('arrive')`, BUKAN diketik ulang. Versi pertama
   * pesan ini menulis "Tiba di lokasi" — tombol dengan nama itu tidak pernah ada di aplikasi;
   * yang ada berlabel "Sampai di lokasi". Owner mencarinya dan tidak menemukannya. Petunjuk yang
   * menyebut nama tombol yang salah lebih buruk daripada tidak ada petunjuk: ia mengirim orang
   * mencari sesuatu yang tidak ada, dan membuat aplikasinya tampak lebih rusak dari keadaannya.
   *
   * Tombol itu ada di layar RINCIAN TUGAS, bukan di layar ini — karena itu kalimatnya menyebut
   * tempatnya juga.
   */
  if (requirements.statusTugas !== undefined && requirements.statusTugas !== 'arrived') {
    blockers.push({
      code: 'not_arrived',
      message: `Buka rincian tugas dan tekan "${formatAction('arrive')}" lebih dulu sebelum menyelesaikan tugas.`,
    });
  }

  if (slots.some(isBusy)) {
    blockers.push({
      code: 'upload_in_progress',
      message: 'Masih ada berkas yang sedang diunggah. Tunggu sampai selesai.',
    });
  }

  const failed = slots.filter((s) => s.phase === 'failed');
  if (failed.length > 0) {
    blockers.push({
      code: 'upload_failed',
      message: `${failed.length} berkas gagal diunggah. Ulangi berkas yang gagal sebelum menyelesaikan.`,
    });
  }

  // Berkas yang masih `idle` juga menghalangi — ia sudah dipilih pengguna tetapi belum terkirim.
  // Menyelesaikan sekarang berarti membuang bukti yang kurir kira sudah ikut.
  const pending = slots.filter((s) => s.phase === 'idle');
  if (pending.length > 0) {
    blockers.push({
      code: 'upload_in_progress',
      message: `${pending.length} berkas belum diunggah. Unggah dulu sebelum menyelesaikan.`,
    });
  }

  if (requirements.requiresProof) {
    if (requirements.recipientName.trim().length === 0) {
      blockers.push({ code: 'recipient_missing', message: 'Isi nama penerima.' });
    }
    /*
     * Tanda tangan OPSIONAL — keputusan Owner 27 Agustus 2026: "tanda tangan tidak usah saja,
     * yang penting foto".
     *
     * KOMENTAR DI SINI DULU KELIRU, dan kekeliruannya menahan seluruh alur selama seminggu.
     * Ia menyimpulkan "server tidak pernah menuntutnya" dari `@IsOptional` pada DTO — padahal DTO
     * hanya memeriksa BENTUK, dan `delivery.service.ts` menolaknya SESUDAH validasi lolos. Jadi
     * selama itu layar tidak pernah meminta tanda tangan sementara server menuntutnya: kurir
     * mengunggah foto, menekan Selesaikan, dan ditolak TASK-006 karena sesuatu yang layarnya
     * sendiri tidak pernah minta. Ditemukan 3 September 2026 pada percobaan lapangan pertama yang
     * unggah fotonya berhasil.
     *
     * Server sekarang benar-benar tidak menuntutnya, dan itu dijaga uji di `proof-upload.spec.ts`
     * yang MEMBACA `delivery.service.ts`. Memeriksa satu lapis lalu menyimpulkan tentang lapis
     * lain adalah cara dua sisi menyimpang tanpa satu pun uji merah.
     * Pad tanda tangannya TETAP ADA — kurir yang memang mengambil tanda tangan tetap dapat
     * melampirkannya; yang dilepas hanya kewajibannya.
     */
    if (
      requirements.requiresSignature &&
      !slots.some((s) => s.kind === 'signature' && isUploaded(s))
    ) {
      blockers.push({
        code: 'signature_missing',
        message: 'Tanda tangan penerima wajib untuk menyelesaikan tugas ini.',
      });
    }
    if (!slots.some((s) => s.kind === 'photo' && isUploaded(s))) {
      blockers.push({
        code: 'photo_missing',
        message: 'Minimal satu foto bukti wajib untuk menyelesaikan tugas ini.',
      });
    }
  }

  return blockers;
}

/** Gerbang tunggal yang dipakai tombol Selesaikan. */
export function canComplete(
  slots: readonly UploadSlot[],
  requirements: ProofRequirements,
): boolean {
  return completionBlockers(slots, requirements).length === 0;
}

/** `fileId` yang dikirim saat `complete` — hanya dari berkas yang benar-benar terunggah. */
export function uploadedPhotoIds(slots: readonly UploadSlot[]): readonly string[] {
  return slots
    .filter((s) => s.kind === 'photo' && isUploaded(s))
    .map((s) => s.fileId)
    .filter((id): id is string => id !== null);
}

export function uploadedSignatureId(slots: readonly UploadSlot[]): string | null {
  return slots.find((s) => s.kind === 'signature' && isUploaded(s))?.fileId ?? null;
}

// ── Validasi pra-unggah ─────────────────────────────────────────────────────

export const ALLOWED_PHOTO_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const SIGNATURE_MIME = 'image/png';

export interface UploadLimits {
  readonly maxPhotoBytes: number;
  readonly maxSignatureBytes: number;
  readonly maxPhotos: number;
}

/** Cerminan batas server (`UPLOAD_MAX_*`), dipakai agar penolakan tidak menunggu perjalanan HTTP. */
export const DEFAULT_LIMITS: UploadLimits = {
  maxPhotoBytes: 5 * 1024 * 1024,
  maxSignatureBytes: 1024 * 1024,
  maxPhotos: 5,
};

/**
 * Menolak berkas SEBELUM diunggah.
 *
 * Ini pemeriksaan kenyamanan, bukan keamanan: server memvalidasi MIME SEBENARNYA lewat magic
 * bytes, jadi berkas yang menyamar tetap ditolak di sana. UI tidak boleh mengklaim berhasil
 * lebih dulu hanya karena ekstensinya benar.
 */
export function validateBeforeUpload(
  file: { readonly mimeType: string; readonly sizeBytes: number },
  kind: 'photo' | 'signature',
  existingPhotos: number,
  limits: UploadLimits = DEFAULT_LIMITS,
): string | null {
  if (kind === 'signature') {
    if (file.mimeType !== SIGNATURE_MIME) {
      return 'Tanda tangan harus berupa PNG.';
    }
    if (file.sizeBytes > limits.maxSignatureBytes) {
      return 'Berkas tanda tangan terlalu besar.';
    }
    return null;
  }

  if (!(ALLOWED_PHOTO_MIME as readonly string[]).includes(file.mimeType)) {
    return 'Format foto tidak didukung. Pakai JPEG, PNG, atau WebP.';
  }
  if (file.sizeBytes > limits.maxPhotoBytes) {
    const mb = Math.round(limits.maxPhotoBytes / (1024 * 1024));
    return `Foto melebihi ${mb} MB. Ambil ulang dengan resolusi lebih rendah.`;
  }
  if (existingPhotos >= limits.maxPhotos) {
    return `Maksimum ${limits.maxPhotos} foto per percobaan.`;
  }
  return null;
}
